import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Check } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { useAppContext } from "../../store/appContext";

interface ICookie {
  consent_necessary: boolean;
  consent_func_cookie: boolean;
  consent_performance: boolean;
  consent_adv_and_mark: boolean;
}

const CookieConsent = () => {
  const getCookie = (name: string) => {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  };

  const setCookie = (name: string, value: boolean, days: number) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
  };

  const updateConsentMode = (prefs: ICookie) => {
    // Use the global updateConsent function from index.html
    if (typeof (window as any).updateConsent === "function") {
      (window as any).updateConsent(prefs);
    }
  };

  const { state, updateState } = useAppContext();
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(true);
  const [preferences, setPreferences] = useState<ICookie>({
    consent_necessary: true, // Always true and disabled
    consent_func_cookie: getCookie("consent_func_cookie")
      ? getCookie("consent_func_cookie") === "true"
      : false,
    consent_performance: getCookie("consent_performance")
      ? getCookie("consent_performance") === "true"
      : false,
    consent_adv_and_mark: getCookie("consent_adv_and_mark")
      ? getCookie("consent_adv_and_mark") === "true"
      : false,
  });

  useEffect(() => {
    // Check if user has already set cookie preferences
    const hasConsent = getCookie("consent_necessary");
    if (!hasConsent) {
      updateState("showCookieSettings", true);
    } else {
      setShowPrivacyNotice(false);
    }
  }, []);

  const handleSavePreferences = () => {
    // Save preferences to cookies
    Object.entries(preferences).forEach(([key, value]) => {
      setCookie(`${key}`, value, 365);
    });

    // Update consent mode in GTM
    updateConsentMode(preferences);

    // Hide the consent modal
    updateState("showCookieSettings", false);
    setShowPrivacyNotice(false);
  };

  const handleAcceptAll = () => {
    const allEnabled: ICookie = {
      ...preferences,
      consent_performance: true,
      consent_adv_and_mark: true,
      consent_func_cookie: true,
    };
    setPreferences(allEnabled);
    Object.entries(allEnabled).forEach(([key, value]) => {
      setCookie(`${key}`, value, 365);
    });

    // Update consent mode in GTM
    updateConsentMode(allEnabled);

    updateState("showCookieSettings", false);
    setShowPrivacyNotice(false);
  };

  const handleRejectAll = () => {
    const allDisabled: ICookie = {
      ...preferences,
      consent_performance: false,
      consent_adv_and_mark: false,
      consent_func_cookie: false,
    };
    setPreferences(allDisabled);
    Object.entries(allDisabled).forEach(([key, value]) => {
      setCookie(`${key}`, value, 365);
    });

    // Update consent mode in GTM
    updateConsentMode(allDisabled);

    updateState("showCookieSettings", false);
    setShowPrivacyNotice(false);
  };

  return (
    <>
      {/* <AlertDialog open={state.showCookieSettings || false}>
        <AlertDialogContent
          className={showPrivacyNotice ? "max-w-2xl" : "max-w-6xl"}
        >
          {showPrivacyNotice ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-xl">
                  Privacy Preferences
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <div className="my-4 text-left">
                    Lilly collects information about your online interactions,
                    such as user ID, browsing history, geolocation and IP
                    address, through tracking technologies, such as third-party
                    cookies. You may accept, decline or withdraw your consent to
                    most cookies and tracking technologies on this site, other
                    than those that are necessary for the site operations and
                    functionality, by clicking one of the options below. We use
                    this information to personalize content, save your
                    preferences, and track website performance. We share
                    information with our analytics, marketing, and advertising
                    partners for purposes of targeted advertising and analyzing
                    website metrics. Please visit our&nbsp;
                    <a
                      className="text-body-text-lilly_red underline"
                      href="https://www.lillyhub.com/legal/lillyusa/cookienotice.html"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Cookie Notice
                    </a>
                    ,&nbsp;
                    <a
                      className="text-body-text-lilly_red underline"
                      href="https://privacynotice.lilly.com/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Statement
                    </a>
                    &nbsp; and&nbsp;
                    <a
                      className="text-body-text-lilly_red underline"
                      href="https://www.lillyhub.com/legal/lillyusa/chpn.html"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Consumer Health Privacy Notice
                    </a>
                    &nbsp; for more information. Certain laws require consent to
                    collect and share information. You can withdraw your consent
                    at any time through the&nbsp;
                    <span
                      className="text-body-text-lilly_red underline cursor-pointer"
                      onClick={() => {
                        setShowPrivacyNotice(false);
                        updateState("showCookieSettings", true);
                      }}
                    >
                      Cookie Settings
                    </span>
                    &nbsp;or&nbsp;
                    <a
                      className="text-body-text-lilly_red underline"
                      href="https://privacynotice.lilly.com/#california-residents"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Your Privacy Choices
                    </a>
                    &nbsp;in our website footer.
                  </div>
                  <div>
                    <p
                      className="text-body-text-lilly_red mt-4 hover:underline cursor-pointer font-semibold"
                      onClick={handleRejectAll}
                    >
                      Users located in Washington or Nevada must use this link
                      to Decline All
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>

              <div className="w-full items-center flex gap-4">
                <Button variant="secondary" size="lg" onClick={handleAcceptAll}>
                  Accept All
                </Button>
                <Button variant="secondary" size="lg" onClick={handleRejectAll}>
                  Reject All
                </Button>
              </div>

              <div>
                <div
                  className="text-body-text-lilly_red underline cursor-pointer"
                  onClick={() => {
                    setShowPrivacyNotice(false);
                    updateState("showCookieSettings", true);
                  }}
                >
                  Manage Cookie Settings
                </div>
              </div>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-xl">
                  Manage Your Cookie Settings
                </AlertDialogTitle>
                <AlertDialogDescription>
                  For more information about Lilly's cookies, tracking
                  technologies and third-party cookies, see our&nbsp;
                  <a
                    className="text-body-text-lilly_red underline"
                    href="https://www.lillyhub.com/legal/lillyusa/cookienotice.html"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Cookie Notice
                  </a>
                  .
                </AlertDialogDescription>
              </AlertDialogHeader>

              <div className="space-y-4 my-4 max-h-[60vh] overflow-y-auto">
                <div className="flex items-start justify-between">
                  <div className="text-base md:w-[80%] w-[60%]">
                    <div className="font-semibold">
                      Strictly Necessary Cookies
                    </div>
                    <p className="text-sm text-gray-500">
                      These cookies are necessary for the website to function
                      properly and cannot be switched off in our systems.
                      Strictly necessary cookies cannot be disabled. For
                      example, these cookies may enable us to optimize page and
                      content loading, distribute and manage site traffic,
                      detect bugs and errors and block malicious traffic and
                      suspicious activity. In addition, these cookies may enable
                      third party-supported features that you use, such as video
                      players and chat tools, and other key site functionality,
                      such as webforms.
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-500">Always On</span>
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <Check
                        size={18}
                        strokeWidth={2.5}
                        className="text-white"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-start justify-between">
                  <div className="text-base md:w-[80%] w-[60%]">
                    <div className="font-semibold">Functional Cookies</div>
                    <p className="text-sm text-gray-500">
                      These cookies and similar technologies remember choices
                      you make such as language or search parameters. We use
                      these cookies to improve your user experience and to make
                      your use of the websites more tailored. If you do not
                      allow these cookies, then some or all of these services
                      may not function as designed.
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-500">
                      {preferences.consent_func_cookie ? "On" : "Off"}
                    </span>
                    <Switch
                      checked={preferences.consent_func_cookie}
                      onCheckedChange={(e) =>
                        setPreferences({
                          ...preferences,
                          consent_func_cookie: e,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="flex items-start justify-between">
                  <div className="text-base md:w-[80%] w-[60%]">
                    <div className="font-semibold">
                      Performance Cookies (Analytics)
                    </div>
                    <p className="text-sm text-gray-500">
                      These cookies allow Lilly to analyze visits and traffic
                      sources so we can measure and improve the performance of
                      our sites. They help us to know which pages are the most
                      and least popular, and see how visitors move around the
                      site to improve the site's performance.
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-500">
                      {preferences.consent_performance ? "On" : "Off"}
                    </span>
                    <Switch
                      checked={preferences.consent_performance}
                      onCheckedChange={(e) =>
                        setPreferences({
                          ...preferences,
                          consent_performance: e,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="flex items-start justify-between">
                  <div className="text-base md:w-[80%] w-[60%]">
                    <div className="font-semibold">
                      Advertising and Marketing Cookies
                    </div>
                    <p className="text-sm text-gray-500">
                      These cookies and similar technologies enable our
                      advertising and marketing partners to provide behavioral
                      advertising and use functionalities like social media
                      pixels to track your online activity across social
                      platforms and websites, which can identify you, as
                      described in the Privacy Statement and Consumer Health
                      Privacy Notice. These methods create a profile of your
                      activity for our business and commercial purposes and
                      enable Lilly and our advertising partners to customize
                      offers and information (online or in person) to you based
                      on your profile, and profiles of persons with similar
                      interests, that may enhance your interactions with Lilly.
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-500">
                      {preferences.consent_adv_and_mark ? "On" : "Off"}
                    </span>
                    <Switch
                      checked={preferences.consent_adv_and_mark}
                      onCheckedChange={(e) =>
                        setPreferences({
                          ...preferences,
                          consent_adv_and_mark: e,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <AlertDialogFooter>
                <div className="flex gap-4 justify-end">
                  <Button
                    variant={"secondary"}
                    onClick={() => handleSavePreferences()}
                  >
                    Save Preferences
                  </Button>

                  <Button variant={"secondary"} onClick={handleAcceptAll}>
                    Accept All
                  </Button>
                </div>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog> */}
    </>
  );
};

export default CookieConsent;
